/**
 * Time maths for the pickers, kept apart from the components that draw them.
 *
 * All of it is pure and all of it is tested, because every bug in here is
 * invisible: an off-by-fifteen-degrees, or a 12 that should have been a 0,
 * produces a perfectly plausible-looking time that is simply the wrong one.
 * Nobody proofreads a number that came out of a control they just clicked.
 */

// ---------------------------------------------------------------------------
// Clock face geometry
// ---------------------------------------------------------------------------

/** Degrees clockwise from twelve o'clock, 0..360. */
export function angleAt(dx: number, dy: number): number {
  const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  return (deg + 360) % 360;
}

/**
 * Which hour a click lands on, 1..12.
 *
 * Twelve sits at the top, so the index that rounds to 0 is 12 rather than 0.
 * That single case is the one everybody gets wrong.
 */
export function hour12At(dx: number, dy: number): number {
  const idx = Math.round(angleAt(dx, dy) / 30) % 12;
  return idx === 0 ? 12 : idx;
}

/** Minutes, snapped to the nearest one. Never 60 - that is 0 an hour later. */
export function minuteAt(dx: number, dy: number): number {
  return Math.round(angleAt(dx, dy) / 6) % 60;
}

// ---------------------------------------------------------------------------
// Minute-of-day  <->  what a human reads
// ---------------------------------------------------------------------------

/** 0..1439 minute-of-day to "HH:MM". The wire format, and what <input type=time> wants. */
export function minutesToHhmm(mins: number): string {
  const v = clampMinuteOfDay(mins);
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
}

/** "HH:MM" back to minute-of-day. Anything unparseable is midnight, never NaN. */
export function hhmmToMinutes(v: string): number {
  const [a, b] = String(v).split(':');
  const h = Number(a);
  const m = Number(b);
  return clampMinuteOfDay((Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0));
}

/** Wraps rather than saturates: a window is a ring, so 24:00 is 00:00. */
export function clampMinuteOfDay(mins: number): number {
  if (!Number.isFinite(mins)) return 0;
  return ((Math.round(mins) % 1440) + 1440) % 1440;
}

/** Minute-of-day split the way a US reader says it. 00:xx is 12 AM, not 0 AM. */
export function to12h(mins: number): { h12: number; m: number; pm: boolean } {
  const v = clampMinuteOfDay(mins);
  const h24 = Math.floor(v / 60);
  const pm = h24 >= 12;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { h12, m: v % 60, pm };
}

/** The inverse. 12 AM is midnight and 12 PM is noon - the two that trip people. */
export function from12h(h12: number, m: number, pm: boolean): number {
  const base = (h12 % 12) + (pm ? 12 : 0);
  return clampMinuteOfDay(base * 60 + m);
}

/** "2:00 AM". What the field shows when it is closed. */
export function formatClock(mins: number | null | undefined): string {
  if (mins == null || !Number.isFinite(mins)) return 'not set';
  const { h12, m, pm } = to12h(mins);
  return `${h12}:${String(m).padStart(2, '0')} ${pm ? 'PM' : 'AM'}`;
}

// ---------------------------------------------------------------------------
// Durations. A different thing from a time of day, and deliberately so:
// "13 hours" is a real answer where "1 PM" is not.
// ---------------------------------------------------------------------------

/** hours -> {days, hours, minutes}, rounded to the minute. */
export function splitHours(total: number | null | undefined): { d: number; h: number; m: number } {
  if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) return { d: 0, h: 0, m: 0 };
  const mins = Math.round(total * 60);
  return { d: Math.floor(mins / 1440), h: Math.floor((mins % 1440) / 60), m: mins % 60 };
}

/** The inverse. Null when the whole thing is zero - the RPO is off, not instant. */
export function joinHours(d: number, h: number, m: number): number | null {
  const mins = Math.max(0, d) * 1440 + Math.max(0, h) * 60 + Math.max(0, m);
  return mins <= 0 ? null : Math.round(mins) / 60;
}

export function describeDuration(total: number | null | undefined): string {
  const { d, h, m } = splitHours(total);
  if (d === 0 && h === 0 && m === 0) return 'not set';
  const bits: string[] = [];
  if (d) bits.push(`${d} day${d === 1 ? '' : 's'}`);
  if (h) bits.push(`${h} hour${h === 1 ? '' : 's'}`);
  if (m) bits.push(`${m} minute${m === 1 ? '' : 's'}`);
  return bits.join(' ');
}

/** The short form for a button face: "4h 30m", "2d 6h", "not set". */
export function shortDuration(total: number | null | undefined): string {
  const { d, h, m } = splitHours(total);
  if (d === 0 && h === 0 && m === 0) return 'not set';
  const bits: string[] = [];
  if (d) bits.push(`${d}d`);
  if (h) bits.push(`${h}h`);
  if (m) bits.push(`${m}m`);
  return bits.join(' ');
}
